import { useTranslation } from 'react-i18next'
import i18n from '@/utils/i18n'
import { leadStatusLabelKey, type InspectionLeadStatus, type LeadPipeline } from '@/types/inspection-lead'

const STATUS_CLASSES: Record<InspectionLeadStatus, string> = {
  new: 'border-[rgba(247,147,26,0.2)] bg-[rgba(247,147,26,0.12)] text-[color:var(--stryzh-color-accent-orange)]',
  in_progress: 'border-blue-500/30 bg-blue-500/10 text-blue-300',
  awaiting_payment: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300',
  paid: 'border-green-500/30 bg-green-500/10 text-green-300',
  cancelled: 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.05)] text-[color:var(--stryzh-color-text-secondary)]',
  // СберАвто
  oo_review: 'border-violet-500/30 bg-violet-500/10 text-violet-300',
  order_created: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
  lot_ready: 'border-sky-500/30 bg-sky-500/10 text-sky-300',
  auction: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300',
  approval: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  done: 'border-green-500/30 bg-green-500/10 text-green-300',
  not_sold: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
}

export function StatusBadge({
  status,
  pipeline,
  label,
}: {
  status: InspectionLeadStatus
  pipeline?: LeadPipeline | null
  /** Готовая подпись вместо подписи воронки (кабинет партнёра). */
  label?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      className={
        'inline-flex shrink-0 items-center rounded-[100px] border px-2 py-0.5 ' +
        'font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] ' +
        'font-medium uppercase tracking-[0.5px] ' +
        STATUS_CLASSES[status]
      }
    >
      {label ?? t(leadStatusLabelKey('status', status, pipeline, (k) => i18n.exists(k)))}
    </span>
  )
}

/**
 * Бэйдж источника лида (значение `inspection_leads.source`). Подсвечивает
 * канал Avito (включая AvitoTest) — это основной поток входящих заявок,
 * фронт-офис должен быстро отличать его от ручных лидов и лендинг-лидов.
 * Остальные источники (LandingDirect, manual и т.п.) — нейтральный серый.
 */
export function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation()
  const isAvito = /avito/i.test(source)
  const isSber = /^sberauto$/i.test(source)
  const cls = isAvito
    ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300'
    : isSber
      ? 'border-green-500/40 bg-green-500/10 text-green-300'
      : 'border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.04)] text-[color:var(--stryzh-color-text-secondary)]'
  return (
    <span
      className={
        'inline-flex shrink-0 items-center rounded-[100px] border px-2 py-0.5 ' +
        'font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] ' +
        'font-medium uppercase tracking-[0.5px] ' +
        cls
      }
    >
      {isSber ? t('leads.pipeline.sberauto') : source}
    </span>
  )
}

/** Тестовый лид: яркая метка (решение Sage 05.11), чтобы не путать с боевыми. */
export function TestBadge() {
  const { t } = useTranslation()
  return (
    <span
      title={t('leads.testBadgeTitle')}
      className="inline-flex shrink-0 items-center rounded-full bg-[#ff2d55] px-2.5 py-0.5 text-[length:var(--stryzh-font-size-xs)] font-bold uppercase tracking-wider text-white"
    >
      {t('leads.testBadge')}
    </span>
  )
}
